import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeDoubleClickFillDownBox,
  computeEvaluatedFillDownDestCells,
  computeFillDownBox,
  computeFillDownRenderBox,
  computeIsFillDownActive,
  computeIsFillDownEnabled,
  computeIsFillDownInvalid,
  computeIsFillDownLocked,
} from 'notebook/tables/store/logic/TableFillDownLogic';
import {computeLockedWarningMessage} from 'notebook/tables/store/logic/TableLockingLogic';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';

import {
  resolveEditWhileTableLockedWarning,
  resolveGetEditWhileCellLockedWarning,
  TableLockingModel,
} from './TableLockingSelectors';
import {TableSelectionModel} from './TableSelectionSelectors';

/**
 * Model containing fill-down state for a table.
 * Fill-down allows users to drag a handle to repeat values from a selection
 * into adjacent cells below.
 */
export interface TableFillDownModel {
  /**
   * Whether fill-down is enabled for this table as a feature.
   * True when enableFillDown prop is set or formulas are not disabled.
   */
  isEnabled: boolean;

  /**
   * Whether a fill-down operation is currently active (user is dragging the fill handle).
   */
  isActive: boolean;

  /**
   * Whether the current fill-down operation is invalid (e.g. dragging over merged regions).
   */
  isInvalid: boolean;

  /**
   * The fill-down box representing the range of cells being filled.
   * Null/undefined when no fill-down operation is in progress.
   */
  box: MultiSelectionBox | null | undefined;

  /**
   * The renderable fill-down box with the selection overlap subtracted.
   * Null when the fill-down box is fully within the selection or when there
   * is no active fill-down.
   */
  renderBox: MultiSelectionBox | null;

  /**
   * The double-click fill-down box: the current selection expanded downward
   * through any following blank, non-merged rows. Used by performDoubleClickFillDown
   * to determine the fill target range.
   * Null when there is no selection.
   */
  doubleClickBox: MultiSelectionBox | null;

  /**
   * Whether the fill-down operation is locked due to the table being locked
   * or any destination cells (cells in the fill-down box outside the selection)
   * being locked or in an archived row.
   */
  isLocked: boolean;

  /**
   * The warning message to surface when a locked fill-down is attempted, scoped
   * to the fill-down box. Null when the fill-down is not locked or when no
   * message applies.
   */
  lockedWarningMessage: string | null;
}

/**
 * Creates a memoized selector that computes the TableFillDownModel from the store.
 */
export function createTableFillDownSelector(
  tableSelectionSelector: (store: TableEditableStore) => TableSelectionModel,
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  tableLockingSelector: (store: TableEditableStore) => TableLockingModel
): (store: TableEditableStore) => TableFillDownModel {
  return createTableModelSelector(
    [
      (store) => store.stableProps.enableFillDown,
      (store) => store.stableProps.disableFormulas ?? false,
      (store) => store.state.multiExtra.fillDownActive,
      (store) => store.state.multiExtra.fillDownInvalid,
      (store) => store.state.multiExtra.fillDownBox,
      (store) => tableSelectionSelector(store).expandedBox,
      (store) => tableSelectionSelector(store).hasMultiSelection,
      (store) => volatilePropsSelector(store).table.table,
      (store) => volatilePropsSelector(store).computedTable,
      (store) => volatilePropsSelector(store).table,
      (store) => volatilePropsSelector(store).table.rowData,
      (store) => tableLockingSelector(store).isTableLocked,
      (store) => store.stableProps.editWhileTableLockedWarning,
      (store) => store.stableProps.getEditWhileCellLockedWarning,
    ],
    (
      enableFillDown,
      disableFormulas,
      fillDownActive,
      fillDownInvalid,
      fillDownBox,
      expandedBox,
      hasMultiSelection,
      tableCells,
      computedTable,
      table,
      rowData,
      isTableLocked,
      editWhileTableLockedWarningProp,
      getEditWhileCellLockedWarningProp
    ) => {
      const fillDownDestCells = computeEvaluatedFillDownDestCells(
        fillDownBox,
        hasMultiSelection,
        expandedBox,
        computedTable,
        table
      );
      const box = computeFillDownBox(fillDownBox);
      const isLocked = computeIsFillDownLocked(isTableLocked, fillDownDestCells, rowData);
      const lockedWarningMessage = computeLockedWarningMessage({
        editingIsLocked: isLocked,
        isTableLocked,
        editWhileTableLockedWarning: resolveEditWhileTableLockedWarning(editWhileTableLockedWarningProp),
        getEditWhileCellLockedWarning: resolveGetEditWhileCellLockedWarning(
          getEditWhileCellLockedWarningProp
        ),
        selection: box ?? null,
      });
      return {
        isEnabled: computeIsFillDownEnabled(enableFillDown, disableFormulas),
        isActive: computeIsFillDownActive(fillDownActive),
        isInvalid: computeIsFillDownInvalid(fillDownInvalid),
        box,
        renderBox: computeFillDownRenderBox(fillDownBox, expandedBox),
        doubleClickBox: computeDoubleClickFillDownBox(tableCells, computedTable, expandedBox),
        isLocked,
        lockedWarningMessage,
      };
    }
  );
}
